import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import SEO from '../SEO';
import { FloatingWhatsApp, WhatsAppCTA } from '../WhatsAppButton';
import { Reveal } from '../Reveal';
import { useLang } from '../../i18n';

export const SERVICES = [
    { id: 'branding', path: '/servicios/branding' },
    { id: 'digitalizacion', path: '/servicios/digitalizacion' },
    { id: 'automatizacion', path: '/servicios/automatizacion' },
    { id: 'seo-sem', path: '/servicios/seo-sem' },
    { id: 'growth-partner', path: '/servicios/growth-partner' },
];

export const PageShell = ({ titleKey, descKey, path, waKey, children }) => {
    const { t } = useLang();
    return (
        <div className="grain bg-canvas text-ink min-h-screen">
            <SEO title={t(titleKey)} description={t(descKey)} path={path} />
            <Header />
            <main>{children}</main>
            <Footer />
            <FloatingWhatsApp message={t(waKey)} />
        </div>
    );
};

export const ServiceHero = ({ eyebrowKey = 'sv_eyebrow', h1Key, introKey, testid }) => {
    const { t } = useLang();
    return (
        <section
            className="relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #E2F1F8 0%, #FFF8E7 48%, #FCE7F0 100%)' }}
            data-testid={testid}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20">
                <Reveal>
                    <p className="font-ink text-[11px] uppercase tracking-[0.3em] text-crimson">{t(eyebrowKey)}</p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-semibold leading-[1.06] text-ink mt-5 max-w-4xl">
                        {t(h1Key)}
                    </h1>
                </Reveal>
                {introKey && (
                    <Reveal delay={0.16}>
                        <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-ink/75">{t(introKey)}</p>
                    </Reveal>
                )}
            </div>
        </section>
    );
};

export const ServiceGrid = ({ items, testid, cols = 'lg:grid-cols-3' }) => (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-6 mt-12`} data-testid={testid}>
        {items.map((it, i) => (
            <Reveal key={it.title} delay={(i % 3) * 0.07} className="h-full">
                <article className="group h-full rounded-[1.4rem] border border-ink/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-crimson/40 hover:shadow-[0_30px_60px_-30px_rgba(27,42,74,0.35)]">
                    <div className="w-12 h-12 rounded-xl bg-pastelblue flex items-center justify-center text-ink group-hover:bg-crimson group-hover:text-pastelcream transition-colors">
                        <it.icon size={22} strokeWidth={1.75} />
                    </div>
                    <h3 className="font-display text-xl font-semibold text-ink mt-5">{it.title}</h3>
                    <p className="mt-3 text-[15px] text-ink/70 leading-relaxed">{it.desc}</p>
                </article>
            </Reveal>
        ))}
    </div>
);

export const CTAFinal = ({ text, message, label, secondary, testid }) => {
    const { t } = useLang();
    return (
        <section className="py-20 lg:py-24" data-testid={testid}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <div className="rounded-[1.6rem] bg-ink text-pastelcream p-9 lg:p-14 relative overflow-hidden">
                        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-crimson/25 blur-3xl" />
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug max-w-3xl relative">{text}</h2>
                        {secondary && <p className="mt-4 text-pastelcream/70 max-w-2xl relative">{secondary}</p>}
                        <div className="mt-9 relative">
                            <WhatsAppCTA message={message} label={label || t('wa_btn')} big />
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export const ServiceLinks = ({ currentId }) => {
    const { t } = useLang();
    return (
        <section className="pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <p className="font-ink text-[10px] uppercase tracking-[0.3em] text-ink/45 mb-5">{t('sp_explora')}</p>
                    <div className="flex flex-wrap gap-3">
                        {SERVICES.filter((s) => s.id !== currentId).map((s) => (
                            <Link
                                key={s.id}
                                to={s.path}
                                className="group inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] text-ink/70 hover:border-crimson hover:text-crimson transition-colors"
                                data-testid={`servicios-link-${s.id}`}
                            >
                                {t(`sp_name_${s.id}`)}
                                <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                            </Link>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
